import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import en from './locales/en.json';

export const SUPPORTED_LANGUAGES = ['en', 'de', 'sr', 'pl', 'tr'];

// redux-persist writes the auth slice here: `localPersistConfig.key = 'local'` in app/store.ts,
// plus redux-persist's own `persist:` prefix. Every field inside is separately JSON-encoded,
// which is why reading one takes two parses.
const AUTH_STORAGE_KEY = 'persist:local';

// Only `en` is bundled eagerly: it is 6 KB and it is the fallback, so it has to be present
// synchronously or every `t()` before the real dictionary lands would render a raw key.
//
// The other four are ~850 KB of JSON combined (de 269 KB, sr 259 KB, pl 163 KB, tr 160 KB) and
// used to be `require()`d into `resources` at module scope, which meant a German user parsed the
// Serbian, Polish and Turkish dictionaries on the main thread before first paint. They are now
// fetched one at a time, on demand, through the backend below.
//
// The `require()` calls also had to go for the Vite migration: Vite serves source as ESM, where
// `require` is not defined at runtime.
const loaders = {
    de: () => import('./locales/de.json'),
    sr: () => import('./locales/sr.json'),
    pl: () => import('./locales/pl.json'),
    tr: () => import('./locales/tr.json'),
};

// Minimal i18next backend that resolves a language from a dynamic import instead of a network
// request, so the dictionaries stay part of the build (hashed, cacheable chunks) rather than
// becoming runtime fetches against a server that would need to host them.
const dynamicImportBackend = {
    type: 'backend',
    init: () => { },
    read: (language, namespace, callback) => {
        const load = loaders[language];

        // `en` is already in `resources`; anything unknown falls back to it.
        if (!load) {
            callback(null, {});
            return;
        }

        load()
            .then((module) => callback(null, module.default ?? module))
            .catch((error) => callback(error, null));
    },
};

/**
 * The active language is the user's own setting — `currentCulture` on the auth slice, populated
 * from `employeeSettings.culture` at login and by the nav-menu language switcher. It is already
 * persisted to localStorage, so it can be read before React mounts, which lets the *right*
 * dictionary be the first one fetched instead of booting into English and correcting a moment
 * later. English is not a sensible default for most of this user base.
 *
 * Degrades to `en` if the entry is missing or malformed (first-ever visit, cleared storage, a
 * future change to the persist config). login-display.tsx then calls changeLanguage() once the
 * store rehydrates, exactly as it did before — so the worst case is the old behaviour, not a break.
 */
function readPersistedCulture() {
    try {
        const raw = localStorage.getItem(AUTH_STORAGE_KEY);
        if (!raw) return null;

        const culture = JSON.parse(JSON.parse(raw).currentCulture);
        return SUPPORTED_LANGUAGES.includes(culture) ? culture : null;
    } catch {
        return null;
    }
}

/**
 * Resolves once the initial dictionary is in place. Awaited in `index.tsx` before the first
 * render so a returning non-English user doesn't see a frame of English before their language
 * loads — the dictionaries are async now, where they used to be bundled synchronously.
 */
export const i18nReady = i18n
    .use(dynamicImportBackend)
    .use(initReactI18next)
    .init({
        fallbackLng: 'en',
        lng: readPersistedCulture() ?? 'en',
        resources: {
            en: {
                translation: en
            }
        },
        // Tells i18next to keep using the backend even though `resources` is non-empty —
        // without this the bundled `en` would be treated as the complete resource set.
        partialBundledLanguages: true,
        supportedLngs: SUPPORTED_LANGUAGES,
        ns: ['translation'],
        defaultNS: 'translation',
        interpolation: {
            escapeValue: false
        },
        pluralSeparator: '_',
        react: {
            // Loading a dictionary is now asynchronous, and this app has a single Suspense
            // boundary (around the route tree) that does not cover the chrome in `App.tsx`.
            // Without this, a language switch would suspend components outside it. `t()` instead
            // returns the `en` fallback until the real bundle arrives, then re-renders.
            useSuspense: false
        },
    });

export default i18n;
